import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import {
  conversationAttentionPath,
  conversationAttentionSchema,
  type ConversationAttention,
} from '../shared/conversation-attention'
import {
  markAllNotificationsReadSchema,
  notificationFeedSchema,
  notificationFilterSchema,
  notificationMutationSchema,
  type MarkAllNotificationsRead,
  type NotificationFeed,
  type NotificationFilter,
  type NotificationMutation,
} from './notification-center-contracts'

export type NotificationCenterRepository = {
  // The outstanding-work count the badge shows, shared with Overview.
  getAttention: (tenantId: string) => Promise<ConversationAttention>
  getFeed: (
    tenantId: string,
    filter: NotificationFilter,
    cursor?: string
  ) => Promise<NotificationFeed>
  markRead: (
    tenantId: string,
    notificationId: string
  ) => Promise<NotificationMutation>
  markAllRead: (tenantId: string) => Promise<MarkAllNotificationsRead>
  dismiss: (
    tenantId: string,
    notificationId: string
  ) => Promise<NotificationMutation>
}

type RepositoryOptions = {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}

function collectionEndpoint(tenantId: string) {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/notifications`
}

function itemEndpoint(
  tenantId: string,
  notificationId: string,
  operation: 'read' | 'dismiss'
) {
  return `${collectionEndpoint(tenantId)}/${encodeURIComponent(notificationId)}/${operation}`
}

export function createNotificationCenterRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: RepositoryOptions): NotificationCenterRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  return {
    getAttention(tenantId) {
      return client.request(conversationAttentionPath(tenantId), {
        method: 'GET',
        responseSchema: conversationAttentionSchema,
      })
    },
    getFeed(tenantId, filter, cursor) {
      const params = new URLSearchParams({
        filter: notificationFilterSchema.parse(filter),
        pageSize: '25',
        version: '2',
      })
      if (cursor) params.set('cursor', cursor)
      return client.request(`${collectionEndpoint(tenantId)}?${params}`, {
        method: 'GET',
        responseSchema: notificationFeedSchema,
      })
    },
    markRead(tenantId, notificationId) {
      return client.request(itemEndpoint(tenantId, notificationId, 'read'), {
        method: 'POST',
        responseSchema: notificationMutationSchema,
      })
    },
    markAllRead(tenantId) {
      return client.request(collectionEndpoint(tenantId), {
        method: 'POST',
        responseSchema: markAllNotificationsReadSchema,
      })
    },
    dismiss(tenantId, notificationId) {
      return client.request(itemEndpoint(tenantId, notificationId, 'dismiss'), {
        method: 'POST',
        responseSchema: notificationMutationSchema,
      })
    },
  }
}
